import { Code, ConnectError } from '@connectrpc/connect'
import {
  IntradayOrderWriteBlockedReason,
  IntradayOrderWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import { volumeTotalLimitMessage } from '~/models/volumeTotalLimitMessage'

/**
 * 全 entry が operation-scoped (画面上部の Alert / トースト)。 受付開始時刻は
 * 受渡日全体に掛かる制約で、 札作成モーダルの単一入力欄に 1:1 で帰属しないため。
 */
const INTRADAY_WRITE_BLOCKED_REASON_KEYS: Record<
  IntradayOrderWriteBlockedReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [IntradayOrderWriteBlockedReason.UNSPECIFIED]: undefined,
  [IntradayOrderWriteBlockedReason.BEFORE_ACCEPTANCE_START]: {
    key: 'page:biddingIntraday.serverError.reason.beforeAcceptanceStart',
  },
  // AC-BIN-015: 提出確認モーダルは違反明細 (件数・軸・設定値) を個別に組み立てて
  // 表示するため (~/pages/IntradayBidding/models/intradaySubmitViolation.ts)、
  // ここでの汎用文言は RPC 直叩き等でモーダルを経由しない経路のフォールバックに
  // とどまる。
  [IntradayOrderWriteBlockedReason.BID_LIMIT_VIOLATION]: {
    key: 'page:biddingIntraday.serverError.reason.bidLimitViolation',
  },
  // AC-BIN-005b: 戦略モードの段の適用日時が「開場時刻 ≤ 適用日時 < GC」 を外れた。
  // 入力段の事前チェック (`strategySchedule.ts`) が段ごとに欄脇へ出すため、 ここは
  // 事前チェックをすり抜けた (= 開いたまま時間が経った等の) 経路のフォールバック。
  // 段が複数あり単一入力欄に帰属しないので operation-scoped。
  [IntradayOrderWriteBlockedReason.SCHEDULE_APPLIED_AT_OUT_OF_WINDOW]: {
    key: 'page:biddingIntraday.serverError.reason.scheduleAppliedAtOutOfWindow',
  },
  // AC-BIN-015 / AC-BIN-052: 上限数量(コマ計) 超過。 提出確認モーダルの明細は
  // 上と同じ経路で組むため、 ここもモーダルを経由しない経路のフォールバック。
  [IntradayOrderWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION]: {
    key: 'page:biddingIntraday.serverError.reason.bidVolumeTotalLimitViolation',
  },
  // AC-BIN-004b (#9213): 札作成モーダルが見ていたプレビューと作成時の設定・手数料単価が違う。
  [IntradayOrderWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED]: {
    key: 'page:biddingIntraday.serverError.reason.feeAutoConsiderChanged',
  },
  // AC-BIN-104: 作成時に初めて値域外と分かった (プレビュー後に入札制限等が変わった)。
  // 札作成モーダルはプレビューを取り直して 2 行の文言を出し直す。
  [IntradayOrderWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE]: {
    key: 'page:biddingIntraday.serverError.reason.feeAdjustedPriceOutOfRange',
  },
  // §エラーハンドリング「フォーミュラ変数の取得失敗」「フォーミュラ計算エラー」 (AC-BIN-020)。
  [IntradayOrderWriteBlockedReason.FORMULA_VARIABLE_UNAVAILABLE]: {
    key: 'page:biddingIntraday.serverError.reason.formulaVariableUnavailable',
  },
  [IntradayOrderWriteBlockedReason.FORMULA_NOT_CALCULABLE]: {
    key: 'page:biddingIntraday.serverError.reason.formulaNotCalculable',
  },
  // AC-BIN-095 / AC-BIN-116: 承認の時点で自動取消時刻を過ぎている。 承認は実行されず札は
  // 承認待ちのまま残るので、 承認者に差戻しで見直す手順を案内する (AC-BIN-052 の承認操作時)。
  [IntradayOrderWriteBlockedReason.APPROVAL_WITHDRAW_AT_PASSED]: {
    key: 'page:biddingIntraday.serverError.reason.approvalWithdrawAtPassed',
  },
  // AC-BIN-116: 一括提出では専用の結果表示に集約するが、RPC を単体で扱う経路は
  // reason registry のフォールバックで利用者に原因を返す。
  [IntradayOrderWriteBlockedReason.SUBMIT_WITHDRAW_AT_PASSED]: {
    key: 'page:biddingIntraday.serverError.reason.submitWithdrawAtPassed',
  },
  // AC-BIN-116: 一括提出は送信直前に申請ブロックへ切り替えるので、ここに来るのは
  // 再判定から backend の判定までに時刻を過ぎた札だけ。
  [IntradayOrderWriteBlockedReason.REQUEST_WITHDRAW_AT_PASSED]: {
    key: 'page:biddingIntraday.serverError.reason.requestWithdrawAtPassed',
  },
  // AC-BIN-099: 承認・差戻しの時点で対象コマの GC を過ぎている。 待っても解けないので、
  // 汎用の「再度お試しください」 にせず GC 経過を伝える (承認案件一覧は GC の事前判定を
  // 持たず、 この理由だけで文言を決める)。
  [IntradayOrderWriteBlockedReason.APPROVAL_GC_PASSED]: {
    key: 'page:biddingIntraday.serverError.reason.approvalGcPassed',
  },
  // AC-BIN-004: 発注主体区分 × エリアから受渡契約コードを決められない (広域機関に未登録)。
  // InvalidArgument で届くが、 待っても直らないので汎用の「再度お試しください」 にせず、
  // 開き直しと管理者への確認依頼を案内する。 一括作成 (AC-BIN-006b) も 1 札ごとの作成なので同じ文言。
  [IntradayOrderWriteBlockedReason.DELIVERY_CONTRACT_CODE_UNRESOLVED]: {
    key: 'page:biddingIntraday.serverError.reason.deliveryContractCodeUnresolved',
  },
  // AC-BIN-115: 「下書きに戻す」 を押した時に送信 worker が送信の最中。 文言は #11944 Q2 の仮採用。
  [IntradayOrderWriteBlockedReason.BID_SEND_IN_FLIGHT]: {
    key: 'page:biddingIntraday.serverError.reason.bidSendInFlight',
  },
}

/**
 * 下書きの保存 (UpdateBiddingIntradayOrder) 用の registry。 手数料自動考慮の 2 理由
 * (AC-BIN-004b / AC-BIN-104) とフォーミュラの 2 理由 (AC-BIN-020) は作成と同じ reason で
 * 届くが、 作成の文言をそのまま出すと「作成できませんでした」「もう一度作成してください」 と
 * 案内してしまうので、 「保存」 の文言に差し替える。 他の理由は作成と同じ。
 */
const INTRADAY_UPDATE_BLOCKED_REASON_KEYS: Record<
  IntradayOrderWriteBlockedReason,
  AppErrorInfoReasonEntry | undefined
> = {
  ...INTRADAY_WRITE_BLOCKED_REASON_KEYS,
  [IntradayOrderWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED]: {
    key: 'page:biddingIntraday.serverError.reasonUpdate.feeAutoConsiderChanged',
  },
  [IntradayOrderWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE]: {
    key: 'page:biddingIntraday.serverError.reasonUpdate.feeAdjustedPriceOutOfRange',
  },
  [IntradayOrderWriteBlockedReason.FORMULA_VARIABLE_UNAVAILABLE]: {
    key: 'page:biddingIntraday.serverError.reasonUpdate.formulaVariableUnavailable',
  },
  [IntradayOrderWriteBlockedReason.FORMULA_NOT_CALCULABLE]: {
    key: 'page:biddingIntraday.serverError.reasonUpdate.formulaNotCalculable',
  },
}

/**
 * 業務の拒否 (FailedPrecondition / InvalidArgument) に付いた `google.rpc.ErrorInfo` detail の reason から理由別
 * の文言 key を引く。 detail が無い / reason が未知 (= backend 先行デプロイ) /
 * registry entry が `undefined` の場合は undefined を返し、 呼び出し元が従来の
 * 汎用文言へフォールバックする。
 *
 * 受付前 (AC-BIN-089c) と GC 超過はどちらも FailedPrecondition で届くので、
 * この引き分けが無いと「待てば出せる」 拒否に「GC を過ぎた」 と表示してしまう。
 */
const INTRADAY_VOLUME_TOTAL_KEYS = {
  detail:
    'page:biddingIntraday.serverError.reason.bidVolumeTotalLimitViolation',
  plain:
    'page:biddingIntraday.serverError.reason.bidVolumeTotalLimitViolationPlain',
}

function intradayReasonMessage(
  err: ConnectError,
  t: TFunction,
  keys: Record<
    IntradayOrderWriteBlockedReason,
    AppErrorInfoReasonEntry | undefined
  >,
): string | undefined {
  const info = readAppErrorInfo(err, IntradayOrderWriteBlockedReasonSchema)
  if (
    info?.reason ===
    IntradayOrderWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION
  ) {
    return volumeTotalLimitMessage(info.metadata, t, INTRADAY_VOLUME_TOTAL_KEYS)
  }
  return resolveAppErrorInfoMessage(
    err,
    IntradayOrderWriteBlockedReasonSchema,
    keys,
    t,
  )?.message
}

export function intradayBlockedReasonMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return intradayReasonMessage(err, t, INTRADAY_WRITE_BLOCKED_REASON_KEYS)
}

/** 業務の拒否として個別の文言を持つ code。 これ以外は「時間をおいて再度お試しください」 に倒す。 */
const INTRADAY_REJECT_CODES: ReadonlySet<Code> = new Set([
  Code.Aborted,
  Code.AlreadyExists,
  Code.NotFound,
  Code.InvalidArgument,
  Code.PermissionDenied,
  Code.FailedPrecondition,
])

/**
 * 時間をおけば通る失敗か (通信の不調・サーバーの想定外エラー等)。
 * `translateIntradayServerError` が想定外エラーの文言に倒す条件と同じ
 * (`intradayServerError.test.ts` で全 code について同期を固定している)。
 */
export function isIntradayRetryLaterError(err: unknown): boolean {
  return !(err instanceof ConnectError) || !INTRADAY_REJECT_CODES.has(err.code)
}

/**
 * 時間前入札 (IntradayOrder) の保存・状態遷移要求で受け取る ConnectError を
 * 画面表示用の日本語文言に訳す (spec intraday-bidding.md AC-058。
 * spot の `translateSpotWriteError` と同一区分・同一 code 分岐)。
 */
export function translateIntradayServerError(
  err: unknown,
  t: TFunction,
): string {
  if (err instanceof ConnectError) {
    switch (err.code) {
      case Code.Aborted:
        return t('page:biddingIntraday.serverError.aborted')
      case Code.AlreadyExists:
        return t('page:biddingIntraday.serverError.alreadyExists')
      case Code.NotFound:
        return t('page:biddingIntraday.serverError.notFound')
      case Code.InvalidArgument:
        // フォーミュラの ÷ 0 (FORMULA_NOT_CALCULABLE) は InvalidArgument で届く。
        return (
          intradayBlockedReasonMessage(err, t) ??
          t('page:biddingIntraday.serverError.invalidArgument')
        )
      case Code.PermissionDenied:
        return t('page:biddingIntraday.serverError.permissionDenied')
      case Code.FailedPrecondition:
        return (
          intradayBlockedReasonMessage(err, t) ??
          t('page:biddingIntraday.serverError.failedPrecondition')
        )
      default:
        return t('page:biddingIntraday.serverError.unknown')
    }
  }
  return t('page:biddingIntraday.serverError.unknown')
}

/**
 * 時間前入札の下書きの保存の失敗を、 画面表示用の日本語文言に訳す (AC-058)。 手数料自動考慮と
 * フォーミュラの理由だけ「保存」 の文言にし、 他は `translateIntradayServerError` と同じ。
 * フォーミュラの ÷ 0 (FORMULA_NOT_CALCULABLE) は InvalidArgument で届くので、 その code も引く。
 */
export function translateIntradayUpdateError(
  err: unknown,
  t: TFunction,
): string {
  if (
    err instanceof ConnectError &&
    (err.code === Code.FailedPrecondition || err.code === Code.InvalidArgument)
  ) {
    const message = intradayReasonMessage(
      err,
      t,
      INTRADAY_UPDATE_BLOCKED_REASON_KEYS,
    )
    if (message !== undefined) return message
  }
  return translateIntradayServerError(err, t)
}
